import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatError } from "@/api";
import { formatDeadline } from "@/lib/utils";
import { toast } from "sonner";
import UserAvatar from "@/components/UserAvatar";
import {
    Plus, KanbanSquare, Users, Building2, ExternalLink, Pencil, Trash2, Check,
    Briefcase, Code2, Palette, Megaphone, Wrench, Globe, Zap, Target,
    Shield, Rocket, Database, Cpu, Headphones, Star, BarChart2, ClipboardList,
    Calendar, FolderOpen, Layers, Package, Activity, FileText, ShoppingCart,
    Search, X as XIcon, Filter, Pin, PinOff, GripVertical
} from "lucide-react";

const ENTITY_ICONS = [
    { key: "users", Icon: Users }, { key: "briefcase", Icon: Briefcase },
    { key: "code", Icon: Code2 }, { key: "palette", Icon: Palette },
    { key: "megaphone", Icon: Megaphone }, { key: "wrench", Icon: Wrench },
    { key: "globe", Icon: Globe }, { key: "zap", Icon: Zap },
    { key: "target", Icon: Target }, { key: "shield", Icon: Shield },
    { key: "rocket", Icon: Rocket }, { key: "database", Icon: Database },
    { key: "cpu", Icon: Cpu }, { key: "headphones", Icon: Headphones },
    { key: "star", Icon: Star }, { key: "bar_chart", Icon: BarChart2 },
    { key: "clipboard", Icon: ClipboardList }, { key: "calendar", Icon: Calendar },
    { key: "folder", Icon: FolderOpen }, { key: "layers", Icon: Layers },
    { key: "package", Icon: Package }, { key: "activity", Icon: Activity },
    { key: "kanban", Icon: KanbanSquare }, { key: "file_text", Icon: FileText },
    { key: "shopping_cart", Icon: ShoppingCart },
];

function getEntityIcon(iconKey, DefaultIcon, size = 15) {
    const found = ENTITY_ICONS.find(i => i.key === iconKey);
    const IconComp = (found?.Icon) || DefaultIcon;
    return <IconComp size={size} />;
}

function IconPicker({ value, onChange }) {
    return (
        <div>
            <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-2">Pictogramă</label>
            <div className="grid grid-cols-9 gap-1.5">
                {ENTITY_ICONS.map(({ key, Icon }) => (
                    <button key={key} type="button" onClick={() => onChange(key)}
                        data-testid={`icon-pick-${key}`}
                        className={`w-8 h-8 rounded-md flex items-center justify-center transition-all
                            ${value === key ? "bg-[#002FA7] text-white shadow-sm" : "bg-[#F8F9FA] text-[#6B7280] hover:bg-[#EEF2FF] hover:text-[#002FA7]"}`}>
                        <Icon size={15} />
                    </button>
                ))}
            </div>
        </div>
    );
}

const COLUMN_COLORS = { todo: "#6B7280", in_progress: "#3B82F6", review: "#F59E0B", done: "#10B981" };
const COLUMN_LABELS = { todo: "De făcut", in_progress: "În lucru", review: "Review", done: "Finalizat" };

// Culoarea labelului de status pe cardul de task din Workspace.
// Preferă culoarea coloanei (din backend), apoi fallback pe cheie.
const STATUS_COLORS = { todo: "#6B7280", in_progress: "#3B82F6", review: "#F59E0B", done: "#10B981", standby: "#EF4444", ctc: "#8B5CF6", recurrent: "#0EA5E9" };
function statusColor(task) {
    return task.column_color || STATUS_COLORS[task.column_key] || "#6B7280";
}

// Reordonează o sub-listă vizibilă (displayedIds) în interiorul unei liste complete (fullArr),
// păstrând pe loc id-urile care NU sunt vizibile (filtrate). Mută elementul din fromIdx în toIdx
// în ordinea afișată, apoi reconstruiește lista completă substituind doar sloturile vizibile.
function reorderSubset(fullArr, displayedIds, fromIdx, toIdx) {
    const newDisplayed = Array.from(displayedIds);
    const [moved] = newDisplayed.splice(fromIdx, 1);
    if (moved === undefined) return fullArr;
    newDisplayed.splice(toIdx, 0, moved);
    const dset = new Set(displayedIds);
    const queue = [...newDisplayed];
    return fullArr.map(itemId => (dset.has(itemId) ? queue.shift() : itemId));
}

export default function WorkspacePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = ["super_admin", "admin"].includes(user?.role);
    const canEditWorkspace = isAdmin; // editare / ștergere workspace = doar admin
    const [workspace, setWorkspace] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCreateBoard, setShowCreateBoard] = useState(false);
    const [boardTemplates, setBoardTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [clearAssignees, setClearAssignees] = useState(false);
    const [showCreateTeam, setShowCreateTeam] = useState(false);
    const [showEditWs, setShowEditWs] = useState(false);
    const [boardForm, setBoardForm] = useState({ name: "", description: "", team_ids: [], icon: "kanban" });
    const [teamForm, setTeamForm] = useState({ name: "", description: "", icon: "users" });
    const [allUsers, setAllUsers] = useState([]);
    const [newTeamMemberIds, setNewTeamMemberIds] = useState([]);
    const [newTeamMemberSearch, setNewTeamMemberSearch] = useState("");
    const [editWsForm, setEditWsForm] = useState({ name: "", description: "" });
    const [saving, setSaving] = useState(false);

    // Team lead în acest workspace are aceleași drepturi ca un admin pentru creare echipe / board-uri / etc.
    const isWsLead = (workspace?.teams || []).some(t => t.is_led_by_me);
    const canManage = isAdmin || isWsLead; // creare echipe, board-uri, etc.

    // Filtre board-uri
    const [boardSearch, setBoardSearch] = useState("");
    const [boardTeamFilter, setBoardTeamFilter] = useState(""); // "" = toate echipele
    const [searchResults, setSearchResults] = useState({ boards: [], tasks: [] });
    const [searchLoading, setSearchLoading] = useState(false);

    // Preferințe personale de afișare board-uri (sincronizate via backend): ordine custom + fixate
    const [boardOrder, setBoardOrder] = useState([]); // ordinea globală a tuturor board-urilor (id-uri)
    const [pinnedBoardIds, setPinnedBoardIds] = useState([]); // id-uri fixate, în ordinea din secțiunea „Fixate"

    // Debounced search: cautăm taskuri prin backend când query >= 2 caractere
    useEffect(() => {
        const q = boardSearch.trim();
        if (q.length < 2) { setSearchResults({ boards: [], tasks: [] }); return; }
        const handle = setTimeout(() => {
            setSearchLoading(true);
            API.get(`/workspaces/${id}/search`, { params: { q } })
                .then(r => setSearchResults(r.data))
                .catch(() => {})
                .finally(() => setSearchLoading(false));
        }, 350);
        return () => clearTimeout(handle);
    }, [boardSearch, id]);

    // Team lead poate crea board doar dacă conduce cel puțin o echipă în acest workspace
    const ledTeams = (workspace?.teams || []).filter(t => t.is_led_by_me);
    const canCreateBoard = isAdmin || ledTeams.length > 0;
    // Workspace-lead poate alege orice echipă din workspace când creează un board (la fel ca admin)
    const boardTeamOptions = (isAdmin || isWsLead) ? (workspace?.teams || []) : ledTeams;

    // Lista de board-uri filtrate
    const filteredBoards = useMemo(() => {
        const all = workspace?.boards || [];
        const q = boardSearch.trim().toLowerCase();
        return all.filter(b => {
            // Filter by team
            if (boardTeamFilter) {
                const tids = b.team_ids || (b.team_id ? [b.team_id] : []);
                if (!tids.includes(boardTeamFilter)) return false;
            }
            // Filter by search (name + description)
            if (q) {
                const hay = `${b.name || ""} ${b.description || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [workspace?.boards, boardSearch, boardTeamFilter]);

    const TEAM_FILTER_KEY = `tom_ws_team_filter_${id}`;
    const selectTeamFilter = (val) => {
        setBoardTeamFilter(val);
        try { localStorage.setItem(TEAM_FILTER_KEY, val); } catch (e) { /* ignore */ }
    };
    const clearFilters = () => { setBoardSearch(""); selectTeamFilter(""); };
    const hasActiveFilter = boardSearch.trim().length > 0 || boardTeamFilter.length > 0;

    // Numărul de board-uri ale unei echipe (pentru badge-urile butoanelor de filtrare)
    const boardCountByTeam = (teamId) => (workspace?.boards || []).filter(b => {
        const tids = b.team_ids || (b.team_id ? [b.team_id] : []);
        return tids.includes(teamId);
    }).length;

    // Selecție inițială echipă: ultima aleasă (din localStorage), altfel prima echipă din workspace.
    const [teamFilterInit, setTeamFilterInit] = useState(false);
    useEffect(() => {
        if (teamFilterInit) return;
        const teams = workspace?.teams || [];
        if (teams.length === 0) return;
        let stored = null;
        try { stored = localStorage.getItem(TEAM_FILTER_KEY); } catch (e) { /* ignore */ }
        if (stored !== null && (stored === "" || teams.some(t => t.id === stored))) {
            setBoardTeamFilter(stored);
        } else {
            setBoardTeamFilter(teams[0].id);
        }
        setTeamFilterInit(true);
    }, [workspace?.teams, teamFilterInit, TEAM_FILTER_KEY]);

    // Inițializează ordinea + fixările din preferințele venite de la backend (sincronizate cross-PC).
    // Rulează la fiecare (re)fetch al workspace-ului; întrucât persistăm imediat orice schimbare,
    // serverul e sursa de adevăr și starea locală se reconstruiește identic.
    useEffect(() => {
        if (!workspace) return;
        const boardIds = (workspace.boards || []).map(b => b.id);
        const prefs = workspace.board_prefs || { pinned: [], order: [] };
        const validOrder = (prefs.order || []).filter(x => boardIds.includes(x));
        const missing = boardIds.filter(x => !validOrder.includes(x));
        setBoardOrder([...validOrder, ...missing]);
        setPinnedBoardIds((prefs.pinned || []).filter(x => boardIds.includes(x)));
    }, [workspace]);

    const persistBoardPrefs = (nextPinned, nextOrder) => {
        API.put(`/workspaces/${id}/board-prefs`, { pinned: nextPinned, order: nextOrder })
            .catch(err => toast.error("Nu am putut salva ordinea: " + formatError(err)));
    };

    const boardById = useMemo(
        () => Object.fromEntries((workspace?.boards || []).map(b => [b.id, b])),
        [workspace?.boards]
    );
    const orderIndex = useMemo(() => {
        const m = {};
        boardOrder.forEach((bid, i) => { m[bid] = i; });
        return m;
    }, [boardOrder]);
    const pinnedSet = useMemo(() => new Set(pinnedBoardIds), [pinnedBoardIds]);

    // Board-uri FIXATE: vizibile mereu (chiar și din alte echipe), respectă doar căutarea. Ordinea = pinnedBoardIds.
    const pinnedBoards = useMemo(() => {
        const q = boardSearch.trim().toLowerCase();
        return pinnedBoardIds
            .map(bid => boardById[bid])
            .filter(Boolean)
            .filter(b => !q || `${b.name || ""} ${b.description || ""}`.toLowerCase().includes(q));
    }, [pinnedBoardIds, boardById, boardSearch]);

    // Board-uri din lista principală: filtrate pe echipă + căutare, FĂRĂ cele fixate, sortate după ordinea custom.
    const mainBoards = useMemo(() => {
        return filteredBoards
            .filter(b => !pinnedSet.has(b.id))
            .slice()
            .sort((a, b) => (orderIndex[a.id] ?? 1e9) - (orderIndex[b.id] ?? 1e9));
    }, [filteredBoards, pinnedSet, orderIndex]);

    const togglePin = (boardId) => {
        const next = pinnedBoardIds.includes(boardId)
            ? pinnedBoardIds.filter(x => x !== boardId)
            : [...pinnedBoardIds, boardId];
        setPinnedBoardIds(next);
        persistBoardPrefs(next, boardOrder);
    };

    const onBoardDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination) return;
        if (source.droppableId !== destination.droppableId) return; // mutare între secțiuni = prin butonul de pin
        if (source.index === destination.index) return;
        if (destination.droppableId === "pinned-boards") {
            const displayed = pinnedBoards.map(b => b.id);
            const next = reorderSubset(pinnedBoardIds, displayed, source.index, destination.index);
            setPinnedBoardIds(next);
            persistBoardPrefs(next, boardOrder);
        } else {
            const displayed = mainBoards.map(b => b.id);
            const next = reorderSubset(boardOrder, displayed, source.index, destination.index);
            setBoardOrder(next);
            persistBoardPrefs(pinnedBoardIds, next);
        }
    };

    const fetchWorkspace = () => {
        setLoading(true);
        API.get(`/workspaces/${id}`)
            .then(r => {
                setWorkspace(r.data);
                setEditWsForm({ name: r.data.name, description: r.data.description || "" });
            })
            .catch(() => toast.error("Workspace negăsit"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchWorkspace(); }, [id]);

    useEffect(() => {
        if (showCreateBoard) {
            API.get("/board-templates").then(r => setBoardTemplates(r.data)).catch(() => setBoardTemplates([]));
        }
    }, [showCreateBoard]);

    // Încarcă utilizatorii când se deschide modalul „Echipă nouă" (pentru selecția inițială de membri)
    useEffect(() => {
        if (showCreateTeam && allUsers.length === 0) {
            API.get("/users").then(r => setAllUsers(r.data)).catch(() => {});
        }
    }, [showCreateTeam, allUsers.length]);

    const createBoard = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            let res;
            if (selectedTemplateId) {
                res = await API.post(`/board-templates/${selectedTemplateId}/create-board`, {
                    name: boardForm.name,
                    workspace_id: id,
                    team_id: boardForm.team_ids[0] || null,
                    clear_assignees: !!clearAssignees,
                });
            } else {
                res = await API.post("/boards", {
                    workspace_id: id,
                    team_ids: boardForm.team_ids,
                    name: boardForm.name,
                    description: boardForm.description,
                    icon: boardForm.icon,
                });
            }
            toast.success(selectedTemplateId ? "Board creat din șablon" : "Board creat");
            setShowCreateBoard(false);
            setBoardForm({ name: "", description: "", team_ids: [], icon: "kanban" });
            setSelectedTemplateId("");
            setClearAssignees(false);
            navigate(`/boards/${res.data.id}`);
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const deleteBoardTemplate = async (tplId, e) => {
        e?.stopPropagation();
        if (!window.confirm("Ștergi acest șablon de board?")) return;
        try {
            await API.delete(`/board-templates/${tplId}`);
            toast.success("Șablon șters");
            setBoardTemplates(boardTemplates.filter(t => t.id !== tplId));
            if (selectedTemplateId === tplId) setSelectedTemplateId("");
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const createTeam = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await API.post("/teams", {
                workspace_id: id,
                name: teamForm.name,
                description: teamForm.description,
                icon: teamForm.icon,
            });
            const newTeamId = res.data?.id;
            // Adaugă membrii selectați (în bulk, dacă există)
            if (newTeamId && newTeamMemberIds.length > 0) {
                try {
                    await API.post(`/teams/${newTeamId}/members`, { user_ids: newTeamMemberIds });
                    toast.success(`Echipă creată cu ${newTeamMemberIds.length} ${newTeamMemberIds.length === 1 ? "membru" : "membri"}`);
                } catch (memErr) {
                    toast.success("Echipă creată");
                    toast.error("Membrii nu au putut fi adăugați: " + formatError(memErr));
                }
            } else {
                toast.success("Echipă creată");
            }
            setShowCreateTeam(false);
            setTeamForm({ name: "", description: "", icon: "users" });
            setNewTeamMemberIds([]);
            setNewTeamMemberSearch("");
            fetchWorkspace();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const saveWorkspace = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await API.patch(`/workspaces/${id}`, editWsForm);
            toast.success("Workspace actualizat");
            setShowEditWs(false);
            fetchWorkspace();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const deleteWorkspace = async () => {
        if (!window.confirm(`Ștergi workspace-ul "${workspace?.name}"? Această acțiune nu poate fi anulată.`)) return;
        try {
            await API.delete(`/workspaces/${id}`);
            toast.success("Workspace șters");
            navigate("/");
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    // Randează o coloană de board (refolosită în secțiunea „Fixate" și în lista principală).
    const renderBoardColumn = (board, { dragHandleProps, isPinned }) => {
        const total = board.task_count || 0;
        const tasks = board.tasks || [];
        return (
            <div data-testid={`board-col-${board.id}`}
                className="glass-panel rounded-2xl flex flex-col" style={{ maxHeight: "calc(100vh - 270px)" }}>
                {/* Header board: mâner drag · titlu (→ pagina board-ului) · fixează */}
                <div className="flex items-center gap-1.5 px-2.5 py-3 border-b border-[#e3e7f5] flex-shrink-0">
                    <span {...dragHandleProps} data-testid={`board-drag-${board.id}`}
                        className="text-[#cbd5e1] hover:text-[#002FA7] cursor-grab active:cursor-grabbing flex-shrink-0 -ml-0.5"
                        title="Trage pentru a reordona">
                        <GripVertical size={16} />
                    </span>
                    <button onClick={() => navigate(`/boards/${board.id}`)}
                        data-testid={`board-card-${board.id}`}
                        className="flex items-center gap-2 flex-1 min-w-0 text-left group">
                        <span className="w-8 h-8 rounded-xl bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center flex-shrink-0">
                            {getEntityIcon(board.icon, KanbanSquare, 16)}
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block font-bold text-sm text-[#111827] truncate group-hover:text-[#002FA7] transition-colors" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{board.name}</span>
                            <span className="block text-[10px] text-[#9CA3AF] font-medium">{total} {total === 1 ? "task" : "taskuri"}</span>
                        </span>
                    </button>
                    <button onClick={() => togglePin(board.id)}
                        data-testid={`board-pin-${board.id}`}
                        title={isPinned ? "Defixează" : "Fixează la început"}
                        className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${isPinned ? "text-[#002FA7] bg-[#002FA7]/10 hover:bg-[#002FA7]/20" : "text-[#cbd5e1] hover:text-[#002FA7] hover:bg-[#002FA7]/10"}`}>
                        {isPinned ? <Pin size={14} className="fill-current" /> : <Pin size={14} />}
                    </button>
                </div>
                {/* Taskuri desfășurate în jos — max ~5 vizibile, restul cu scroll */}
                <div className="flex flex-col gap-2 p-2 overflow-y-auto" style={{ maxHeight: "calc(5 * 4.6rem)" }}>
                    {tasks.map(task => (
                        <button key={task.id} onClick={() => navigate(`/boards/${board.id}?task=${task.id}`)}
                            data-testid={`ws-task-${task.id}`}
                            className="bg-white border border-[#e3e7f5] rounded-xl p-2.5 text-left hover:border-[#002FA7]/40 hover:shadow-sm transition-all group/task">
                            <p className="text-xs font-semibold text-[#111827] leading-snug group-hover/task:text-[#002FA7] transition-colors"
                                style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                {task.title}
                            </p>
                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                                    style={{ backgroundColor: `${statusColor(task)}1a`, color: statusColor(task) }}>
                                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: statusColor(task) }} />
                                    {task.column_name || COLUMN_LABELS[task.column_key] || "—"}
                                </span>
                                {task.priority === "high" && <span className="text-[10px] font-bold text-red-500">Urgent</span>}
                            </div>
                        </button>
                    ))}
                    {tasks.length === 0 && (
                        <div className="text-center py-5 text-[11px] text-[#9CA3AF]">Niciun task.</div>
                    )}
                </div>
            </div>
        );
    };

    if (loading) return (
        <div className="p-6 space-y-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white border border-[#E4E4E7] rounded-md animate-pulse" />)}
        </div>
    );

    return (
        <div className="p-6 max-w-[1500px] mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {/* Header */}
            <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#002FA7] rounded-md flex items-center justify-center flex-shrink-0">
                        <Building2 size={20} className="text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                {workspace?.name}
                            </h1>
                            {canEditWorkspace && (
                                <div className="flex items-center gap-1">
                                    <button onClick={() => setShowEditWs(true)} data-testid="edit-workspace-btn"
                                        className="text-[#9CA3AF] hover:text-[#002FA7] p-1 rounded hover:bg-blue-50 transition-colors">
                                        <Pencil size={13} />
                                    </button>
                                    <button onClick={deleteWorkspace} data-testid="delete-workspace-btn"
                                        className="text-[#9CA3AF] hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors">
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            )}
                        </div>
                        {workspace?.description && <p className="text-sm text-[#9CA3AF]">{workspace.description}</p>}
                    </div>
                </div>
                {(canManage || canCreateBoard) && (
                    <div className="flex gap-2">
                        {canManage && (
                            <button onClick={() => setShowCreateTeam(true)} data-testid="create-team-btn"
                                className="flex items-center gap-1.5 text-xs border border-[#E4E4E7] text-[#4B5563] px-3 py-2 rounded-md hover:bg-[#F8F9FA] transition-colors">
                                <Plus size={14} /> Echipă nouă
                            </button>
                        )}
                        {canCreateBoard && (
                            <button onClick={() => setShowCreateBoard(true)} data-testid="create-board-btn"
                                className="flex items-center gap-1.5 text-xs bg-[#002FA7] text-white px-3 py-2 rounded-md hover:bg-[#002482] transition-colors">
                                <Plus size={14} /> Board nou
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Echipe — strip compact */}
            <div className="glass-panel rounded-2xl px-4 py-3 mb-5">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-[#111827] uppercase tracking-wide mr-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        <Users size={13} className="inline mr-1 -mt-0.5 text-[#002FA7]" />
                        Echipe <span className="text-[#9CA3AF]">({workspace?.teams?.length || 0})</span>
                    </span>
                    {(workspace?.teams || []).map(team => (
                        <button key={team.id} onClick={() => navigate(`/teams/${team.id}`)}
                            data-testid={`team-card-${team.id}`}
                            className="flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full bg-[#f2f3ff] hover:bg-[#e2e7ff] border border-[#e3e7f5] transition-colors group">
                            <span className="w-6 h-6 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center flex-shrink-0">
                                {getEntityIcon(team.icon, Users, 12)}
                            </span>
                            <span className="text-xs font-semibold text-[#111827] group-hover:text-[#002FA7] transition-colors whitespace-nowrap">{team.name}</span>
                            <span className="text-[10px] text-[#9CA3AF] font-medium">{team.member_count || 0}</span>
                        </button>
                    ))}
                    {(workspace?.teams || []).length === 0 && (
                        <span className="text-xs text-[#9CA3AF] italic">Nicio echipă.{canManage ? " Creează prima." : ""}</span>
                    )}
                    {canManage && (
                        <button onClick={() => setShowCreateTeam(true)} data-testid="create-team-btn-list"
                            className="flex items-center gap-1 text-xs text-[#002FA7] hover:bg-[#002FA7]/10 rounded-full px-2 py-1 transition-colors font-semibold" title="Echipă nouă">
                            <Plus size={13} /> Echipă
                        </button>
                    )}
                </div>
            </div>

            {/* Selector echipă pentru filtrarea board-urilor */}
            {(workspace?.teams || []).length > 0 && (
                <div className="glass-panel rounded-2xl px-4 py-3 mb-5" data-testid="board-team-selector">
                    <p className="text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2.5 flex items-center gap-1.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        <Filter size={13} className="text-[#002FA7]" />
                        Alege echipa a căror board-uri vrei să le vezi
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => selectTeamFilter("")} data-testid="team-filter-all"
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors
                                ${boardTeamFilter === "" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-[#f2f3ff] text-[#111827] border-[#e3e7f5] hover:bg-[#e2e7ff]"}`}>
                            Toate echipele
                            <span className={boardTeamFilter === "" ? "text-white/70" : "text-[#9CA3AF]"}>({workspace?.boards?.length || 0})</span>
                        </button>
                        {(workspace?.teams || []).map(team => {
                            const active = boardTeamFilter === team.id;
                            return (
                                <button key={team.id} onClick={() => selectTeamFilter(team.id)}
                                    data-testid={`team-filter-${team.id}`}
                                    className={`flex items-center gap-1.5 pl-1.5 pr-3 py-1 rounded-full text-xs font-semibold border transition-colors
                                        ${active ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-[#f2f3ff] text-[#111827] border-[#e3e7f5] hover:bg-[#e2e7ff]"}`}>
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${active ? "bg-white/20 text-white" : "bg-[#002FA7]/10 text-[#002FA7]"}`}>
                                        {getEntityIcon(team.icon, Users, 12)}
                                    </span>
                                    <span className="whitespace-nowrap">{team.name}</span>
                                    <span className={active ? "text-white/70" : "text-[#9CA3AF]"}>({boardCountByTeam(team.id)})</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Board-uri */}
            <div>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h3 className="text-base font-bold text-[#111827] flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        <KanbanSquare size={18} className="text-[#002FA7]" />
                        Board-uri active {hasActiveFilter
                            ? <span className="text-[#002FA7] font-semibold">({filteredBoards.length}/{workspace?.boards?.length || 0})</span>
                            : <span className="text-[#9CA3AF] font-semibold">({workspace?.boards?.length || 0})</span>
                        }
                    </h3>
                </div>

                {/* Bara de filtre */}
                <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md" data-testid="board-filters">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
                        <input
                            type="text"
                            value={boardSearch}
                            onChange={e => setBoardSearch(e.target.value)}
                            placeholder="Caută board după nume sau descriere..."
                            data-testid="board-search-input"
                            className="w-full pl-8 pr-8 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#0F1317] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                        />
                        {boardSearch && (
                            <button onClick={() => setBoardSearch("")} data-testid="clear-board-search"
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111827] p-0.5">
                                <XIcon size={13} />
                            </button>
                        )}
                    </div>
                    {hasActiveFilter && (
                        <button onClick={clearFilters} data-testid="clear-all-filters"
                            className="flex items-center gap-1 text-xs text-[#9CA3AF] hover:text-[#002FA7] px-2 py-1 transition-colors">
                            <XIcon size={12} /> Resetează filtrele
                        </button>
                    )}
                </div>

                <DragDropContext onDragEnd={onBoardDragEnd}>
                    {/* Secțiunea „Fixate" — board-uri favorite, mereu vizibile (chiar din alte echipe) */}
                    {pinnedBoards.length > 0 && (
                        <div className="mb-4" data-testid="pinned-boards-section">
                            <p className="text-xs font-bold text-[#002FA7] uppercase tracking-wide mb-2 flex items-center gap-1.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                <Pin size={12} className="fill-current" /> Fixate
                            </p>
                            <Droppable droppableId="pinned-boards" direction="horizontal">
                                {(dp) => (
                                    <div ref={dp.innerRef} {...dp.droppableProps}
                                        className="flex gap-4 overflow-x-auto pb-3 items-start" data-testid="pinned-boards-row">
                                        {pinnedBoards.map((board, idx) => (
                                            <Draggable key={board.id} draggableId={board.id} index={idx}>
                                                {(prov, snap) => (
                                                    <div ref={prov.innerRef} {...prov.draggableProps}
                                                        className={`w-72 flex-shrink-0 ${snap.isDragging ? "opacity-90" : ""}`}>
                                                        {renderBoardColumn(board, { dragHandleProps: prov.dragHandleProps, isPinned: true })}
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {dp.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </div>
                    )}

                    {/* Lista principală — board-uri filtrate pe echipă, reordonabile prin drag */}
                    <Droppable droppableId="main-boards" direction="horizontal">
                        {(dp) => (
                            <div ref={dp.innerRef} {...dp.droppableProps}
                                className="flex gap-4 overflow-x-auto pb-3 items-start" data-testid="boards-row">
                                {mainBoards.map((board, idx) => (
                                    <Draggable key={board.id} draggableId={board.id} index={idx}>
                                        {(prov, snap) => (
                                            <div ref={prov.innerRef} {...prov.draggableProps}
                                                className={`w-72 flex-shrink-0 ${snap.isDragging ? "opacity-90" : ""}`}>
                                                {renderBoardColumn(board, { dragHandleProps: prov.dragHandleProps, isPinned: false })}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {dp.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                {mainBoards.length === 0 && pinnedBoards.length === 0 && (
                    <div className="text-center py-10 px-6 text-sm text-[#9CA3AF] border border-dashed border-[#e3e7f5] rounded-2xl w-full" data-testid="no-boards-placeholder">
                        {hasActiveFilter
                            ? <>Niciun board nu corespunde filtrelor. <button onClick={clearFilters} className="text-[#002FA7] font-semibold hover:underline">Resetează filtrele</button>.</>
                            : <>Niciun board.{canManage ? " Creează primul board." : ""}</>
                        }
                    </div>
                )}

                {/* Search results: task-uri găsite */}
                {boardSearch.trim().length >= 2 && (
                    <div className="mt-6" data-testid="task-search-results">
                        <h2 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-widest mb-3 flex items-center gap-2">
                            Task-uri găsite
                            {searchLoading && <span className="text-[10px] font-normal italic normal-case">(caut...)</span>}
                            {!searchLoading && <span className="text-[#002FA7] dark:text-[#6A91FF]">({searchResults.tasks.length})</span>}
                        </h2>
                        {searchResults.tasks.length === 0 && !searchLoading && (
                            <div className="text-center py-6 text-sm text-[#9CA3AF] border border-dashed border-[#E4E4E7] dark:border-[#252B33] rounded-md">
                                Niciun task cu „{boardSearch}" în acest workspace.
                            </div>
                        )}
                        {searchResults.tasks.length > 0 && (
                            <div className="space-y-1.5">
                                {searchResults.tasks.map(task => (
                                    <button key={task.id} onClick={() => navigate(`/boards/${task.board_id}?task=${task.id}`)}
                                        data-testid={`task-search-result-${task.id}`}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md hover:border-[#002FA7]/30 hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A] transition-all text-left group">
                                        <span className="text-[10px] font-mono font-bold text-[#9CA3AF] flex-shrink-0">#{task.task_number}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-[#111827] dark:text-[#F3F4F6] truncate">{task.title}</p>
                                            <p className="text-xs text-[#9CA3AF] truncate">
                                                în <span className="font-semibold">{task.board?.name || "Board"}</span>
                                                {task.priority && <> · <span className={`font-medium ${task.priority === "high" ? "text-red-500" : task.priority === "medium" ? "text-orange-500" : "text-[#9CA3AF]"}`}>{task.priority === "high" ? "Urgent" : task.priority === "medium" ? "Mediu" : "Scăzut"}</span></>}
                                                {task.due_date && <> · Termen: {formatDeadline(task.due_date)}</>}
                                            </p>
                                        </div>
                                        {task.assignees && task.assignees.length > 0 && (
                                            <div className="flex items-center -space-x-1.5 flex-shrink-0">
                                                {task.assignees.slice(0, 3).map(a => (
                                                    <UserAvatar key={a.id} user={a} className="w-6 h-6 border-2 border-white dark:border-[#151A21]" textClassName="text-[10px]" />
                                                ))}
                                                {task.assignees.length > 3 && (
                                                    <div className="w-6 h-6 rounded-full border-2 border-white dark:border-[#151A21] bg-[#9CA3AF] flex items-center justify-center flex-shrink-0">
                                                        <span className="text-white text-[9px] font-bold">+{task.assignees.length - 3}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <ExternalLink size={13} className="text-[#9CA3AF] opacity-0 group-hover:opacity-100 flex-shrink-0" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Edit Workspace Modal */}
            {canEditWorkspace && showEditWs && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowEditWs(false)} />
                    <div className="relative bg-white rounded-md shadow-xl w-full max-w-md mx-4 z-10">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Editează workspace</h2>
                        </div>
                        <form onSubmit={saveWorkspace} className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume *</label>
                                <input autoFocus data-testid="edit-ws-name" value={editWsForm.name}
                                    onChange={e => setEditWsForm({ ...editWsForm, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Descriere</label>
                                <input data-testid="edit-ws-description" value={editWsForm.description}
                                    onChange={e => setEditWsForm({ ...editWsForm, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setShowEditWs(false)} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                                <button data-testid="save-ws-btn" type="submit" disabled={saving}
                                    className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se salvează..." : "Salvează"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Board Modal */}
            {canCreateBoard && showCreateBoard && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreateBoard(false)} />
                    <div className="relative bg-white rounded-md shadow-xl w-full max-w-md mx-4 z-10">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Board nou</h2>
                        </div>
                        <form onSubmit={createBoard} className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume *</label>
                                <input autoFocus data-testid="new-board-name" value={boardForm.name}
                                    onChange={e => setBoardForm({ ...boardForm, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" required />
                            </div>
                            {boardTemplates.length > 0 && (
                                <div>
                                    <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Folosește șablon (opțional)</label>
                                    <div className="space-y-1 max-h-32 overflow-y-auto rounded-md border border-[#E4E4E7] p-1">
                                        <button type="button" onClick={() => setSelectedTemplateId("")}
                                            data-testid="board-tpl-none"
                                            className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center justify-between ${selectedTemplateId === "" ? "bg-[#EEF2FF] text-[#002FA7] font-semibold" : "hover:bg-[#F8F9FA]"}`}>
                                            <span>Fără șablon (4 coloane standard + Stand By)</span>
                                            {selectedTemplateId === "" && <Check size={11} />}
                                        </button>
                                        {boardTemplates.map(t => (
                                            <div key={t.id}
                                                className={`group flex items-center gap-1 px-2 py-1.5 rounded text-xs cursor-pointer ${selectedTemplateId === t.id ? "bg-[#EEF2FF] text-[#002FA7]" : "hover:bg-[#F8F9FA]"}`}
                                                onClick={() => setSelectedTemplateId(t.id)}
                                                data-testid={`board-tpl-${t.id}`}>
                                                <div className="flex-1 min-w-0">
                                                    <div className={`font-semibold truncate ${selectedTemplateId === t.id ? "" : "text-[#111827]"}`}>{t.name}</div>
                                                    <div className="text-[10px] text-[#9CA3AF] truncate">{(t.columns?.length || 0)} coloane · {(t.tasks?.length || 0)} task-uri · {(t.labels?.length || 0)} etichete{t.description ? ` · ${t.description}` : ""}</div>
                                                </div>
                                                {selectedTemplateId === t.id && <Check size={11} className="flex-shrink-0" />}
                                                <button type="button" onClick={(e) => deleteBoardTemplate(t.id, e)}
                                                    data-testid={`board-tpl-delete-${t.id}`}
                                                    className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-red-500 p-0.5 rounded transition-opacity">
                                                    <Trash2 size={11} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    {selectedTemplateId && (
                                        <label className="flex items-start gap-2 mt-2 cursor-pointer">
                                            <input type="checkbox"
                                                data-testid="tpl-clear-assignees"
                                                checked={clearAssignees}
                                                onChange={e => setClearAssignees(e.target.checked)}
                                                className="w-3.5 h-3.5 mt-0.5 accent-[#002FA7]" />
                                            <span className="text-xs text-[#4B5563] leading-tight">
                                                Curăță responsabilii
                                                <span className="block text-[10px] text-[#9CA3AF]">
                                                    Util când refolosești șablonul pe altă echipă. Task-urile vor fi create fără responsabili (le poți atribui după).
                                                </span>
                                            </span>
                                        </label>
                                    )}
                                </div>
                            )}
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Echipe {!isAdmin && ledTeams.length > 0 && <span className="text-[#9CA3AF] normal-case ml-1">(doar echipele pe care le conduci)</span>}</label>
                                {boardTeamOptions.length === 0 ? (
                                    <p className="text-xs text-[#9CA3AF] italic">Nicio echipă disponibilă</p>
                                ) : (
                                    <div className="space-y-1.5 max-h-28 overflow-y-auto rounded-md border border-[#E4E4E7] p-2">
                                        {boardTeamOptions.map(t => (
                                            <label key={t.id} className="flex items-center gap-2 cursor-pointer group">
                                                <input type="checkbox"
                                                    checked={boardForm.team_ids.includes(t.id)}
                                                    onChange={e => {
                                                        if (e.target.checked) {
                                                            setBoardForm({ ...boardForm, team_ids: [...boardForm.team_ids, t.id] });
                                                        } else {
                                                            setBoardForm({ ...boardForm, team_ids: boardForm.team_ids.filter(id => id !== t.id) });
                                                        }
                                                    }}
                                                    className="w-3.5 h-3.5 accent-[#002FA7]"
                                                    data-testid={`board-team-check-${t.id}`}
                                                />
                                                <span className="text-sm text-[#111827]">{t.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <IconPicker value={boardForm.icon} onChange={icon => setBoardForm({ ...boardForm, icon })} />
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setShowCreateBoard(false)} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                                <button data-testid="create-board-submit" type="submit" disabled={saving}
                                    className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se creează..." : "Creează board"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Team Modal */}
            {canManage && showCreateTeam && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => { setShowCreateTeam(false); setNewTeamMemberIds([]); setNewTeamMemberSearch(""); }} />
                    <div className="relative bg-white rounded-md shadow-xl w-full max-w-md mx-4 z-10 flex flex-col max-h-[85vh]" data-testid="create-team-modal">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Echipă nouă</h2>
                        </div>
                        <form onSubmit={createTeam} className="flex flex-col flex-1 min-h-0">
                            <div className="px-5 py-4 space-y-4 overflow-y-auto">
                                <div>
                                    <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume *</label>
                                    <input autoFocus data-testid="new-team-name" value={teamForm.name}
                                        onChange={e => setTeamForm({ ...teamForm, name: e.target.value })}
                                        className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" required />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Descriere</label>
                                    <input data-testid="new-team-description" value={teamForm.description}
                                        onChange={e => setTeamForm({ ...teamForm, description: e.target.value })}
                                        className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                                </div>
                                <IconPicker value={teamForm.icon} onChange={icon => setTeamForm({ ...teamForm, icon })} />
                                {/* Selecție membri inițiali */}
                                <div className="pt-2 border-t border-[#F3F4F6]">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide">Membri inițiali</label>
                                        <span className="text-xs text-[#6B7280]" data-testid="new-team-member-count">
                                            {newTeamMemberIds.length} selectat{newTeamMemberIds.length === 1 ? "" : "i"}
                                        </span>
                                    </div>
                                    <div className="relative mb-2">
                                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                                        <input
                                            data-testid="new-team-member-search"
                                            type="text"
                                            placeholder="Caută după nume sau email..."
                                            value={newTeamMemberSearch}
                                            onChange={e => setNewTeamMemberSearch(e.target.value)}
                                            className="w-full pl-8 pr-8 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
                                        />
                                        {newTeamMemberSearch && (
                                            <button type="button" onClick={() => setNewTeamMemberSearch("")}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4B5563]">
                                                <XIcon size={14} />
                                            </button>
                                        )}
                                    </div>
                                    {(() => {
                                        const q = newTeamMemberSearch.trim().toLowerCase();
                                        const candidates = (allUsers || []).filter(u => u.status !== "deactivated");
                                        const filtered = candidates.filter(u => {
                                            if (!q) return true;
                                            return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                                        });
                                        if (candidates.length === 0) {
                                            return <div className="px-3 py-4 text-center text-xs text-[#6B7280] border border-dashed border-[#E4E4E7] rounded-md">Niciun utilizator disponibil</div>;
                                        }
                                        return (
                                            <div className="border border-[#E4E4E7] rounded-md max-h-56 overflow-y-auto" data-testid="new-team-member-list">
                                                {filtered.length === 0 ? (
                                                    <div className="px-3 py-4 text-center text-xs text-[#6B7280]">Niciun utilizator nu corespunde căutării</div>
                                                ) : filtered.map(u => {
                                                    const isSelected = newTeamMemberIds.includes(u.id);
                                                    return (
                                                        <label
                                                            key={u.id}
                                                            data-testid={`new-team-member-row-${u.id}`}
                                                            className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${isSelected ? "bg-[#EEF2FF]" : "hover:bg-[#F8F9FA]"}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => setNewTeamMemberIds(prev => prev.includes(u.id) ? prev.filter(x => x !== u.id) : [...prev, u.id])}
                                                                data-testid={`new-team-member-checkbox-${u.id}`}
                                                                className="w-4 h-4 accent-[#002FA7] cursor-pointer"
                                                            />
                                                            <div className="w-6 h-6 rounded-full bg-[#002FA7] text-white text-[10px] font-semibold flex items-center justify-center shrink-0">
                                                                {(u.name || u.email || "?").charAt(0).toUpperCase()}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm font-medium text-[#111827] truncate">{u.name || "(fără nume)"}</div>
                                                                <div className="text-[11px] text-[#6B7280] truncate">{u.email}</div>
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })()}
                                    <p className="text-[10px] text-[#9CA3AF] mt-1.5">Opțional — îi poți adăuga și mai târziu din pagina echipei.</p>
                                </div>
                            </div>
                            <div className="flex gap-2 px-5 py-4 border-t border-[#E4E4E7]">
                                <button type="button" onClick={() => { setShowCreateTeam(false); setNewTeamMemberIds([]); setNewTeamMemberSearch(""); }} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                                <button data-testid="create-team-submit" type="submit" disabled={saving}
                                    className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se creează..." : "Creează echipă"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
